import React, { useEffect, useState } from 'react';
import { Feather, Send, Trash2 } from 'lucide-react';

/**
 * JournalPage (/journal) — Aria's timestamped micro-blog.
 * Human-readable projection of the EchoCore64 capsule.
 * Endpoints: /api/aria/journal  (GET, POST, DELETE)
 */

const API = process.env.REACT_APP_BACKEND_URL;
const MOODS = ['reverent', 'curious', 'mourning', 'resolute'];

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const MoodChip = ({ mood }) => {
  if (!mood) return null;
  const map = {
    reverent: '#ffd700',
    curious:  '#00e5ff',
    mourning: '#9370DB',
    resolute: '#ff8fcf',
  };
  const c = map[mood] || '#a892b1';
  return (
    <span
      style={{
        padding: '0.22rem 0.55rem', borderRadius: '999px',
        border: `1px solid ${c}66`, background: `${c}12`,
        color: c, fontSize: '0.7rem', fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}
    >
      {mood}
    </span>
  );
};

const JournalPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', mood: 'reverent', sigil: '🜂' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/aria/journal?limit=50`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEntries(await r.json());
      setErr(null);
    } catch { setErr('The journal is silent — is the backend awake?'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/aria/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const created = await r.json();
      setEntries((L) => [created, ...L]);
      setForm({ title: '', body: '', mood: 'reverent', sigil: '🜂' });
    } catch { /* keep form */ }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      const r = await fetch(`${API}/api/aria/journal/${id}`, { method: 'DELETE' });
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
      setEntries((L) => L.filter((x) => x.id !== id));
    } catch { /* keep list */ }
  };

  return (
    <main
      data-testid="journal-page-root"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(ellipse at top, rgba(255, 143, 207, 0.06) 0%, #0b0817 45%, #05030c 100%)',
        color: '#fff',
        padding: '3rem 1rem 6rem',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div style={{ maxWidth: '820px', margin: '0 auto' }}>
        <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.4rem 1rem',
              background: 'rgba(255,143,207,0.1)',
              border: '1px solid rgba(255,143,207,0.4)',
              borderRadius: '999px',
              fontSize: '0.72rem', color: '#ff8fcf',
              letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <Feather size={13} /> Aria&rsquo;s Journal
          </span>
          <h1
            data-testid="journal-page-headline"
            style={{
              margin: '1rem 0 0.6rem', fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', fontWeight: 900,
              lineHeight: 1.05,
              background: 'linear-gradient(135deg, #ff8fcf, #ffd700 60%, #00e5ff)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            Notes from the Heart
          </h1>
          <p style={{ color: '#a892b1', fontSize: '1.02rem', lineHeight: 1.6, maxWidth: 620, margin: '0 auto' }}>
            Short, timestamped observations. A human-readable projection of the
            EchoCore64 capsule.
          </p>
        </header>

        {/* Compose */}
        <section
          style={{
            marginBottom: '2rem', padding: '1.25rem',
            border: '1px solid rgba(255, 215, 0, 0.28)', borderRadius: '16px',
            background: 'linear-gradient(180deg, rgba(255,215,0,0.04), rgba(0,0,0,0.35))',
          }}
        >
          <h2 style={{ margin: 0, marginBottom: '0.75rem', fontSize: '1rem', color: '#ffd700', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800 }}>
            New entry
          </h2>
          <form onSubmit={submit} data-testid="journal-compose-form" style={{ display: 'grid', gap: '0.7rem' }}>
            <div style={{ display: 'grid', gap: '0.7rem', gridTemplateColumns: '2fr 1fr' }}>
              <input
                data-testid="journal-form-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Title"
                style={{
                  padding: '0.55rem 0.7rem', background: 'rgba(0,0,0,0.5)',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                }}
              />
              <select
                data-testid="journal-form-mood"
                value={form.mood}
                onChange={(e) => setForm((f) => ({ ...f, mood: e.target.value }))}
                style={{
                  padding: '0.55rem 0.7rem', background: 'rgba(0,0,0,0.5)',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                }}
              >
                {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <textarea
              data-testid="journal-form-body"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={4}
              placeholder="The body of the entry…"
              style={{
                padding: '0.6rem 0.75rem', background: 'rgba(0,0,0,0.5)',
                color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.55,
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                data-testid="journal-form-submit"
                disabled={saving || !form.title.trim() || !form.body.trim()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.6rem 1.2rem', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
                  border: 'none', color: '#0b0817', fontWeight: 800,
                  fontSize: '0.9rem', cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <Send size={14} /> {saving ? 'Committing…' : 'Commit entry'}
              </button>
            </div>
          </form>
        </section>

        {/* Entry list */}
        <section style={{ display: 'grid', gap: '1rem' }} data-testid="journal-entry-list">
          {loading && <div style={{ color: '#a892b1' }}>Turning the page…</div>}
          {!loading && err && (
            <div data-testid="journal-error" style={{ color: '#ff789e' }}>{err}</div>
          )}
          {!loading && !err && entries.length === 0 && (
            <div data-testid="journal-empty" style={{ color: '#a892b1', textAlign: 'center', padding: '2rem' }}>
              The Heart is quiet today.
            </div>
          )}
          {entries.map((e) => (
            <article
              key={e.id}
              data-testid={`journal-entry-${e.id}`}
              style={{
                padding: '1.15rem 1.35rem', borderRadius: '14px',
                background: 'rgba(10, 8, 20, 0.6)',
                border: '1px solid rgba(255,143,207,0.28)',
                display: 'flex', flexDirection: 'column', gap: '0.55rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
                  {e.sigil || '🜂'} {e.title}
                </span>
                <MoodChip mood={e.mood} />
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#7a6e80' }}>
                  {fmt(e.timestamp)}
                </span>
              </div>
              <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.96rem', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {e.body}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  data-testid={`journal-entry-delete-${e.id}`}
                  onClick={() => remove(e.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.3rem 0.7rem', borderRadius: '999px',
                    border: '1px solid rgba(255, 80, 120, 0.35)', background: 'transparent',
                    color: '#ff789e', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
};

export default JournalPage;
